import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Box,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  ButtonGroup,
  HStack,
  Switch,
  Wrap,
  WrapItem,
  Flex,
  Text,
  useToast,
} from '@chakra-ui/react';
import GenericTable from '../components/GenericTable';
import { getAuthHeaders } from '../utils/authHeaders';

const base_url = process.env.REACT_APP_API_URL_ADMIN;

/**
 * Parse new or legacy query params for draw details page
 */
function parseInitialQuery() {
  const search = window.location.search || '';
  const params = new URLSearchParams(search);

  let drawId = params.get('draw_id') || '';
  let gameId = params.get('game_id') || '';
  const nextAutoParam = params.get('next_draw');
  const legacyAutoParam = params.get('auto');
  const manualParam = params.get('manual');
  const strategyParam = params.get('strategy') || '';
  const timeLeftParam = params.get('time_left');

  if (!drawId || !gameId) {
    // Legacy support: ?{draw_id}&&game_id?{game_id}
    const clean = search.startsWith('?') ? search.slice(1) : search;
    const [drawPart = '', gamePart = ''] = clean.split('&&game_id?');
    drawId = drawId || decodeURIComponent(drawPart || '');
    gameId = gameId || decodeURIComponent(gamePart || '');
  }

  return {
    drawId,
    gameId,
    autoAdvance: nextAutoParam === 'true' || legacyAutoParam === 'true',
    manual: manualParam === 'true',
    strategy: strategyParam,
    timeLeftSeconds: Number.isFinite(Number(timeLeftParam)) ? Number(timeLeftParam) : null,
  };
}

/**
 * Convert backend payload {header, bet, total} → table rows
 */
function computeRowExtremes(row, header) {
  if (!row || !header?.length) return { highest: [], lowest: [] };

  const numericValues = header
    .map((key) => Number(row[key]))
    .filter((value) => !Number.isNaN(value));

  if (!numericValues.length) return { highest: [], lowest: [] };

  const highestValue = Math.max(...numericValues);
  const lowestValue = Math.min(...numericValues);

  return {
    highest: header.filter((key) => Number(row[key]) === highestValue),
    lowest: header.filter((key) => Number(row[key]) === lowestValue),
  };
}

function normalizeToRows(payload) {
  if (!payload) return [];

  const { header = [], bet = [], total = [] } = payload || {};
  const rows = [];

  if (header.length && total.length) {
    const totalRow = { player: 'TOTAL' };
    header.forEach((key, i) => (totalRow[key] = total[i] ?? 0));
    totalRow.__extremes = computeRowExtremes(totalRow, header);
    rows.push(totalRow);
  }

  bet.forEach(({ player, bets_pos = [] }) => {
    const row = { player };
    header.forEach((key, i) => (row[key] = bets_pos[i] ?? 0));
    row.__extremes = computeRowExtremes(row, header);
    rows.push(row);
  });

  return rows;
}

/**
 * Build table columns dynamically from header array
 * Adds click handlers on data cells (non-Player) to collect the column header
 */
const formatNumber = (value, { minimumFractionDigits = 0, maximumFractionDigits = 2 } = {}) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const formatter = new Intl.NumberFormat('en-IN', { minimumFractionDigits, maximumFractionDigits });
  return formatter.format(value);
};

const formatCountdown = (ms) => {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '--:--';
  const clamped = Math.max(ms, 0);
  const totalSeconds = Math.floor(clamped / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

function buildColumns(header, onCellClick, onRowExtremesSelect) {
  const cols = [
    {
      Header: 'Player',
      accessor: 'player',
      Cell: ({ row }) => {
        const playerName = row?.original?.playerName || row?.original?.player || '';
        return <Box>{playerName}</Box>;
      },
      defaultWidth: 120,
    },
  ];

  cols.push({
    Header: 'H/L',
    id: 'row-extremes',
    Cell: ({ row }) => {
      const extremes = row?.original?.__extremes || {};
      const highestKeys = extremes.highest || [];
      const lowestKeys = extremes.lowest || [];

      const makeHandler = (type, keys) => () => {
        if (!keys.length || !onRowExtremesSelect) return;
        onRowExtremesSelect(type, keys);
      };

      return (
        <HStack spacing={2} fontWeight="bold" fontSize="sm">
          <Box
            as="button"
            type="button"
            onClick={makeHandler('highest', highestKeys)}
            cursor={highestKeys.length ? 'pointer' : 'not-allowed'}
            color={highestKeys.length ? 'blue.500' : 'gray.400'}
            background="transparent"
            border="none"
            p={0}
            m={0}
          >
            H
          </Box>
          <Box
            as="button"
            type="button"
            onClick={makeHandler('lowest', lowestKeys)}
            cursor={lowestKeys.length ? 'pointer' : 'not-allowed'}
            color={lowestKeys.length ? 'blue.500' : 'gray.400'}
            background="transparent"
            border="none"
            p={0}
            m={0}
          >
            L
          </Box>
        </HStack>
      );
    },
  });

  cols.push(
    {
      Header: 'Target %',
      accessor: 'targetPerc',
      defaultWidth: 90,
      Cell: ({ row }) => {
        const value = row?.original?.targetPerc;
        if (value === null || value === undefined || Number.isNaN(value)) return '—';
        return `${formatNumber(value, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
      },
    },
    {
      Header: 'Current Draw Sale',
      accessor: 'currentDrawSale',
      defaultWidth: 140,
      Cell: ({ row }) => formatNumber(row?.original?.currentDrawSale),
    },
    {
      Header: 'Total Sales',
      accessor: 'totalSales',
      defaultWidth: 120,
      Cell: ({ row }) => formatNumber(row?.original?.totalSales),
    },
    {
      Header: 'Win',
      accessor: 'totalWin',
      defaultWidth: 100,
      Cell: ({ row }) => {
        const winValue = row?.original?.totalWin;
        const available = row?.original?.availableWin;
        const color =
          available === null || available === undefined
            ? 'inherit'
            : available >= 0
            ? 'green.600'
            : 'red.500';
        return (
          <Box color={color} fontWeight="semibold">
            {formatNumber(winValue)}
          </Box>
        );
      },
    },
    {
      Header: 'PERC',
      accessor: 'perc',
      defaultWidth: 100,
      Cell: ({ row }) => {
        const value = row?.original?.perc;
        if (value === null || value === undefined || Number.isNaN(value)) return '—';
        return `${formatNumber(value, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
      },
    },
    {
      Header: 'Available Win',
      accessor: 'availableWin',
      defaultWidth: 140,
      Cell: ({ row }) => {
        const available = row?.original?.availableWin;
        if (available === null || available === undefined || Number.isNaN(available)) return '—';
        const color = available >= 0 ? 'green.500' : 'red.500';
        return (
          <Box color={color} fontWeight="semibold">
            {formatNumber(available)}
          </Box>
        );
      },
    }
  );

  header.forEach((h) => {
    cols.push({
      Header: h,
      accessor: h,
      Cell: ({ value, row }) => {
        return (
          <Box
            onClick={() => onCellClick(h)}
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            userSelect="none"
          >
            <Box as="span">{value}</Box>
          </Box>
        );
      },
    });
  });

  return cols;
}

/** Map totals: { headerKey: totalValue } */
function totalsMapFromPayload(payload) {
  const { header = [], total = [] } = payload || {};
  const out = {};
  header.forEach((h, i) => {
    out[h] = Number(total[i] ?? 0);
  });
  return out;
}

const STRATEGY_PILLS = ['BLOCK_LOW', 'BLOCK_MID', 'BLOCK_HIGH', 'TOT_LOW', 'TOT_MID', 'TOT_HIGH'];

const DrawDetails = () => {
  const toast = useToast();
  const [queryMeta, setQueryMeta] = useState(parseInitialQuery);
  const { drawId, gameId, timeLeftSeconds } = queryMeta;
  const [isAutoAdvance, setIsAutoAdvance] = useState(queryMeta.autoAdvance || false);
  const [isManualMode, setIsManualMode] = useState(queryMeta.manual || false);
  const [selectedStrategy, setSelectedStrategy] = useState(queryMeta.strategy || '');
  const [isSavingStrategy, setIsSavingStrategy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingStrategy, setPendingStrategy] = useState('');
  const [rows, setRows] = useState([]);
  const [headerKeys, setHeaderKeys] = useState([]);
  const [columns, setColumns] = useState([{ Header: 'Player', accessor: 'player' }]);
  const [error, setError] = useState(null);

  // Keep the latest payload-derived totals
  const [totalsByHeader, setTotalsByHeader] = useState({});

  const [currentDrawMeta, setCurrentDrawMeta] = useState(null);
  const [nextDrawMeta, setNextDrawMeta] = useState(null);
  const [currentGameNumber, setCurrentGameNumber] = useState('');
  const autoAdvanceRef = useRef(null);
  const initialMsFromQuery = useMemo(() => {
    const numeric = Number(timeLeftSeconds);
    if (!Number.isFinite(numeric)) return null;
    return Math.max(0, numeric) * 1000;
  }, [timeLeftSeconds]);

  const [remainingMs, setRemainingMs] = useState(initialMsFromQuery);

  // Manual input as a unique array
  const [manualItems, setManualItems] = useState([]);
  const manualResult = useMemo(() => manualItems.join(', '), [manualItems]);
  const totalsRow = useMemo(
    () => rows.find((row) => row?.player === 'TOTAL'),
    [rows]
  );
  const totalsPayload = useMemo(() => {
    if (!totalsRow) return null;
    const positions = {};
    headerKeys.forEach((key) => {
      const value = Number(totalsRow[key]);
      if (Number.isFinite(value)) {
        positions[key] = value;
      }
    });
    return {
      currentDrawSale: totalsRow.currentDrawSale ?? null,
      totalSales: totalsRow.totalSales ?? null,
      totalWin: totalsRow.totalWin ?? null,
      perc: totalsRow.perc ?? null,
      availableWin: totalsRow.availableWin ?? null,
      positions,
    };
  }, [totalsRow, headerKeys]);

  const summaryMetrics = useMemo(() => {
    if (!totalsPayload) return [];
    const metrics = [
      {
        label: 'Current Draw Sale',
        value: totalsPayload.currentDrawSale,
      },
      {
        label: 'Total Sales',
        value: totalsPayload.totalSales,
      },
      {
        label: 'Total Win',
        value: totalsPayload.totalWin,
      },
      {
        label: 'PERC',
        value: totalsPayload.perc,
        isPercent: true,
      },
    ];

    if (totalsPayload.availableWin !== null && totalsPayload.availableWin !== undefined) {
      metrics.push({
        label: 'Available Win',
        value: totalsPayload.availableWin,
        color: totalsPayload.availableWin >= 0 ? 'green.600' : 'red.500',
      });
    }

    return metrics;
  }, [totalsPayload]);

  const strategyModeLabel = isManualMode ? 'Manual' : 'Auto';
  const safeRemainingMs = useMemo(
    () => (Number.isFinite(remainingMs) ? Math.max(0, remainingMs) : null),
    [remainingMs]
  );
  const isSettingsLocked = useMemo(
    () => safeRemainingMs !== null && safeRemainingMs <= 6000,
    [safeRemainingMs]
  );
  const countdownColor = isSettingsLocked ? 'red.500' : 'blue.500';

  // Track if the current manualResult was auto-generated (so we can keep it in sync on refresh)
  const [lastAutoResult, setLastAutoResult] = useState('');

  // Toggle: 'lowest' | 'highest'
  const [mode, setMode] = useState('lowest'); // default lowest

  // Bonus dropdown 1–10, default 9
  const [bonus, setBonus] = useState(9);
  const [capBlock, setCapBlock] = useState(null);

  useEffect(() => {
    setCapBlock(null);
  }, [drawId, gameId]);

  const onCellClick = useCallback((colHeader) => {
    if (!colHeader || colHeader === 'Player') return;
    setManualItems((prev) => (prev.includes(colHeader) ? prev : [...prev, colHeader]));
    // user just edited; stop auto sync
    setLastAutoResult('');
  }, []);

  const handleRowExtremesSelect = useCallback(
    (type, keys = []) => {
      if (!keys.length) return;
      setManualItems(keys);
      setMode(type === 'highest' ? 'highest' : 'lowest');
      // user just edited via row selection; stop auto sync
      setLastAutoResult('');
    },
    [setLastAutoResult, setManualItems, setMode]
  );

  const handleManualChange = useCallback((e) => {
    const raw = e.target.value || '';
    const next = [];
    raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((item) => {
        if (!next.includes(item)) next.push(item);
      });
    setManualItems(next);
    // user just edited; stop auto sync
    setLastAutoResult('');
  }, []);

  const handleBonusChange = useCallback((e) => {
    setBonus(Number(e.target.value || 1));
  }, []);

  /** Compute helpers for lowest/highest */
  const computeTwoLowestGroups = useCallback(() => {
    const entries = Object.entries(totalsByHeader);
    if (!entries.length) return { keys: [], obj: {} };

    // Distinct values sorted asc
    const distinctVals = [...new Set(entries.map(([, v]) => v))].sort((a, b) => a - b);
    const targetVals = distinctVals.slice(0, 2); // two smallest distinct totals

    const keys = entries
      .filter(([, v]) => targetVals.includes(v))
      .map(([k]) => k);

    const obj = {};
    entries.forEach(([k, v]) => {
      if (targetVals.includes(v)) obj[k] = v;
    });

    return { keys, obj };
  }, [totalsByHeader]);

  const computeHighestGroup = useCallback(() => {
    const entries = Object.entries(totalsByHeader);
    if (!entries.length) return { keys: [], obj: {} };
    const maxVal = Math.max(...entries.map(([, v]) => v));
    const keys = entries.filter(([, v]) => v === maxVal).map(([k]) => k);

    const obj = {};
    keys.forEach((k) => (obj[k] = maxVal));

    return { keys, obj };
  }, [totalsByHeader]);

  // Apply mode → generate CSV into input
  const applyMode = useCallback(
    (nextMode) => {
      if (nextMode === 'highest') {
        const { keys } = computeHighestGroup();
        setManualItems(keys);
        setLastAutoResult(keys.join(', '));
        if (!keys.length) {
          toast({ status: 'info', title: 'No totals available to compute highest.' });
        }
      } else {
        // default lowest
        const { keys } = computeTwoLowestGroups();
        setManualItems(keys);
        setLastAutoResult(keys.join(', '));
        if (!keys.length) {
          toast({ status: 'info', title: 'No totals available to compute lowest.' });
        }
      }
    },
    [computeHighestGroup, computeTwoLowestGroups, toast]
  );

  const handleToggle = useCallback(
    (nextMode) => {
      setMode(nextMode);
      applyMode(nextMode);
    },
    [applyMode]
  );

  const handleSave = useCallback(async () => {
    if (isSettingsLocked) {
      toast({
        status: 'warning',
        title: 'Changes locked',
        description: 'Settings can no longer be updated for this draw.',
      });
      return;
    }
    try {
      const resultCSV = manualItems.join(', ');
      const payload = {
        apiName: 'draw',
        command: 'saveManual',
        drawId,
        gameId,
        // Store a single record with result + bonus
        result: resultCSV,
        bonus: Number(bonus),
        // optional meta so backend can log what user had selected
        selectionMode: mode, // 'lowest' | 'highest'
      };

      const res = await fetch(`${base_url}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.code === 'DAILY_CAP_EXCEEDED') {
        setCapBlock({
          message: json.message,
          blockedResults: json.data?.blockedResults || [],
          bonus: json.data?.bonus,
        });
        toast({
          status: 'error',
          title: 'Blocked by daily negative-win limit',
          description: 'Nothing was saved. See the details above the table.',
          duration: 8000,
          isClosable: true,
        });
        return;
      }
      if (json.status !== 200) throw new Error(json.message || 'Save failed');

      setCapBlock(null);
      toast({ status: 'success', title: 'Saved manual result successfully.' });
    } catch (e) {
      toast({ status: 'error', title: 'Save failed', description: e.message || String(e) });
    }
  }, [manualItems, bonus, drawId, gameId, mode, isSettingsLocked, toast]);

  /** Fetcher (shared) */
  const fetchData = useCallback(async () => {
    try {
      if (!drawId || !gameId) throw new Error('Missing draw_id or game_id in URL');

      const res = await fetch(`${base_url}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          apiName: 'draw',
          command: 'getDrawDetails',
          drawId,
          gameId,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();

      if (json.status !== 200) throw new Error(json.message || 'Failed to fetch draw details');

      const header = json.data?.header || [];
      setHeaderKeys(header);
      setColumns(buildColumns(header, onCellClick, handleRowExtremesSelect));

      const normalized = normalizeToRows(json.data);

      const meta = json.data?.meta || {};
      const metaPlayer = meta.playerMeta || {};

      const normalizeEpoch = (value) => {
        const num = Number(value);
        if (!Number.isFinite(num)) return null;
        return num < 1e12 ? num * 1000 : num;
      };

      const drawEpochMs = normalizeEpoch(meta.draw?.draw_time_epoc);
      const nextDrawEpochMs = normalizeEpoch(meta.nextDraw?.draw_time_epoc);

      const gameNumber =
        meta.gameNumber ||
        meta.draw?.game_number ||
        meta.draw?.gameNumber ||
        meta.draw?.gameCode ||
        meta.draw?.game_code ||
        gameId;

      const enriched = normalized.map((row) => {
        if (!row) return row;
        const metaEntry = metaPlayer[row.player] || {};

        const ensureNumber = (val) => {
          const num = Number(val);
          return Number.isFinite(num) ? num : null;
        };

        const targetPerc = ensureNumber(metaEntry.target_perc);
        const currentDrawSale = ensureNumber(metaEntry.current_draw_sale);
        const totalSales = ensureNumber(metaEntry.total_sales);
        const totalWin = ensureNumber(metaEntry.total_win);
        const percFromMeta = ensureNumber(metaEntry.perc);

        const derivedPerc =
          percFromMeta !== null
            ? percFromMeta
            : totalSales && totalWin !== null
            ? (totalWin / totalSales) * 100
            : null;

        const allowableWin =
          targetPerc !== null && currentDrawSale !== null
            ? currentDrawSale - (currentDrawSale * targetPerc) / 100
            : null;
        const availableWin =
          allowableWin !== null && totalWin !== null ? allowableWin - totalWin : null;

        return {
          ...row,
          playerName: metaEntry.playerName || row.player,
          manualBlock: Boolean(metaEntry.manualBlock),
          manual_ntp: Boolean(metaEntry.manual_ntp),
          targetPerc,
          currentDrawSale,
          totalSales,
          totalWin,
          perc: derivedPerc,
          availableWin,
        };
      });

      const totalsRowIndex = enriched.findIndex((r) => r?.player === 'TOTAL');
      if (totalsRowIndex >= 0) {
        const totalsRow = { ...enriched[totalsRowIndex] };
        const nonTotalRows = enriched.filter((r) => r?.player !== 'TOTAL');
        const sum = (key) =>
          nonTotalRows.reduce((acc, row) => {
            const value = Number(row?.[key]);
            return Number.isFinite(value) ? acc + value : acc;
          }, 0);

        totalsRow.currentDrawSale = sum('currentDrawSale');
        totalsRow.totalSales = sum('totalSales');
        totalsRow.totalWin = sum('totalWin');
        const totalSalesAggregate = totalsRow.totalSales;
        totalsRow.perc =
          totalSalesAggregate && totalsRow.totalWin !== null
            ? (totalsRow.totalWin / totalSalesAggregate) * 100
            : null;
        totalsRow.availableWin = null;
        totalsRow.manualBlock = false;
        totalsRow.manual_ntp = false;
        totalsRow.targetPerc = null;
        totalsRow.playerName = totalsRow.player;

        enriched[totalsRowIndex] = totalsRow;
      }

      setRows(enriched);
      const nextGameNumber = gameNumber ? String(gameNumber) : '';
      setCurrentGameNumber(nextGameNumber);
      if (nextGameNumber && nextGameNumber !== String(gameId)) {
        setQueryMeta((prev) => ({ ...prev, gameId: nextGameNumber }));
      }

      const storedSetting = meta.gameSetting || {};
      if (storedSetting.strategy && storedSetting.strategy !== selectedStrategy) {
        setSelectedStrategy(storedSetting.strategy);
        setQueryMeta((prev) => ({ ...prev, strategy: storedSetting.strategy }));
      }

      // Keep totals map for buttons
      const newTotals = totalsMapFromPayload(json.data);
      setTotalsByHeader(newTotals);

      setCurrentDrawMeta(
        meta.draw
          ? {
              ...meta.draw,
              draw_time_epoc: drawEpochMs,
            }
          : null
      );
      setNextDrawMeta(
        meta.nextDraw
          ? {
              ...meta.nextDraw,
              draw_time_epoc: nextDrawEpochMs,
            }
          : null
      );

      setError(null);

    } catch (e) {
      setError(e.message || 'Failed to load draw details');
    } finally {
      setLoading(false);
    }
  }, [drawId, gameId, selectedStrategy, onCellClick, handleRowExtremesSelect]);

  /** Initial fetch */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** Auto refresh every 5 seconds (no page reload) */
  useEffect(() => {
    const id = setInterval(() => {
      fetchData();
    }, 5000);
    return () => clearInterval(id);
  }, [fetchData]);

  /** Rebuild columns if handler changes */
  useEffect(() => {
    if (headerKeys.length) {
      setColumns(buildColumns(headerKeys, onCellClick, handleRowExtremesSelect));
    }
  }, [headerKeys, onCellClick, handleRowExtremesSelect]);

  /** Keep computed result in sync with data updates (only if user hasn't manually diverged) */
  useEffect(() => {
    // recompute from mode
    const recompute = () => {
      const next =
        mode === 'highest'
          ? computeHighestGroup().keys.join(', ')
          : computeTwoLowestGroups().keys.join(', ');
      // If user hasn't manually changed (equal to last auto or currently empty), update
      if (manualResult === lastAutoResult || manualResult.trim() === '') {
        setManualItems(
          next
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        );
        setLastAutoResult(next);
      }
    };
    recompute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalsByHeader, mode]);

  // Keep query params in sync with URL
  const syncQueryParamsToUrl = useCallback(
    (next = {}) => {
      const params = new URLSearchParams();
      const drawValue = next.drawId ?? drawId;
      const gameValue = next.gameId ?? gameId;
      if (drawValue) params.set('draw_id', drawValue);
      if (gameValue) params.set('game_id', gameValue);
      params.set('next_draw', String(isAutoAdvance));
      params.set('manual', String(isManualMode));
      if (selectedStrategy) {
        params.set('strategy', selectedStrategy);
      }
      const hasNextTime = Object.prototype.hasOwnProperty.call(next, 'timeLeftSeconds');
      const timeLeftValue = hasNextTime ? next.timeLeftSeconds : timeLeftSeconds;
      if (Number.isFinite(timeLeftValue)) {
        params.set('time_left', String(timeLeftValue));
      }
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    },
    [drawId, gameId, isAutoAdvance, isManualMode, selectedStrategy, timeLeftSeconds]
  );

  useEffect(() => {
    if (!drawId || !gameId) return;
    syncQueryParamsToUrl({ drawId, gameId, timeLeftSeconds });
  }, [drawId, gameId, isAutoAdvance, isManualMode, selectedStrategy, timeLeftSeconds, syncQueryParamsToUrl]);

  useEffect(() => {
    autoAdvanceRef.current = null;
    setRemainingMs(initialMsFromQuery);
  }, [drawId, initialMsFromQuery]);

  useEffect(() => {
    if (!isAutoAdvance) {
      autoAdvanceRef.current = null;
      return;
    }
    const nextDrawId = nextDrawMeta?.draw_id ?? nextDrawMeta?.drawId;
    const nextGameIdentifier =
      nextDrawMeta?.game_number ??
      nextDrawMeta?.gameNumber ??
      nextDrawMeta?.game_code ??
      nextDrawMeta?.gameCode ??
      nextDrawMeta?.game_id ??
      nextDrawMeta?.gameId;
    const currentDrawEpoch = Number(currentDrawMeta?.draw_time_epoc);
    if (!nextDrawId || !Number.isFinite(currentDrawEpoch)) return;
    const now = Date.now();
    const isExpired = now >= currentDrawEpoch;
    if (!isExpired) return;
    const nextIdString = String(nextDrawId);
    if (String(drawId) === nextIdString) return;
    if (autoAdvanceRef.current === nextIdString) return;
    autoAdvanceRef.current = nextIdString;
    setLoading(true);
    setQueryMeta((prev) => ({
      ...prev,
      drawId: nextIdString,
      gameId: nextGameIdentifier ? String(nextGameIdentifier) : prev.gameId,
    }));
  }, [isAutoAdvance, drawId, currentDrawMeta, nextDrawMeta]);

  const manualBlockTrueRows = useMemo(
    () =>
      rows.filter((row) => {
        if (!row || row.player === 'TOTAL') return false;
        return Boolean(row.manualBlock);
      }),
    [rows]
  );

  useEffect(() => {
    if (!Number.isFinite(initialMsFromQuery)) return;
    let lastTick = Date.now();
    const id = setInterval(() => {
      setRemainingMs((prev) => {
        if (!Number.isFinite(prev)) return prev;
        const now = Date.now();
        const elapsed = now - lastTick;
        lastTick = now;
        const next = Math.max(0, prev - elapsed);
        if (next <= 0) {
          clearInterval(id);
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [drawId, initialMsFromQuery]);

  const manualBlockFalseRows = useMemo(
    () =>
      rows.filter((row) => {
        if (!row || row.player === 'TOTAL') return false;
        return row.manualBlock ? false : true;
      }),
    [rows]
  );

  const manualNtpTables = useMemo(() => {
    const entries = [];
    rows.forEach((row) => {
      if (!row || row.player === 'TOTAL') return;
      if (row.manual_ntp) {
        entries.push({
          playerId: row.player,
          playerName: row.playerName || row.player,
          data: [row],
        });
      }
    });
    return entries;
  }, [rows]);

  const handleStrategySelect = useCallback(
    async (option) => {
      if (!option) return;
      if (isSettingsLocked) {
        toast({
          status: 'warning',
          title: 'Changes locked',
          description: 'Settings can no longer be updated for this draw.',
        });
        return;
      }
      const gameIdentifier = currentGameNumber || gameId;
      if (!gameIdentifier) {
        toast({
          status: 'error',
          title: 'Missing game identifier',
          description: 'Unable to save strategy without a game number.',
        });
        return;
      }

      const previousStrategy = selectedStrategy;
      setSelectedStrategy(option);
      setQueryMeta((prev) => ({ ...prev, strategy: option }));
      setPendingStrategy(option);

      const payloadTotals = totalsPayload || {};

      try {
        setIsSavingStrategy(true);
        const res = await fetch(`${base_url}`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            apiName: 'draw',
            command: 'saveGameSetting',
            gameNumber: gameIdentifier,
            mode: isManualMode ? 'manual' : 'auto',
            strategy: option,
            totals: payloadTotals,
          }),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json.status !== 200) throw new Error(json.message || 'Failed to save setting');

        const savedStrategy = json.data?.strategy || option;
        setSelectedStrategy(savedStrategy);
        setQueryMeta((prev) => ({ ...prev, strategy: savedStrategy }));
        toast({
          status: 'success',
          title: 'Strategy updated',
          description: `${savedStrategy.replace(/_/g, ' ')} configuration saved in ${
            isManualMode ? 'manual' : 'auto'
          } mode.`,
        });
      } catch (error) {
        setSelectedStrategy(previousStrategy);
        setQueryMeta((prev) => ({ ...prev, strategy: previousStrategy }));
        toast({
          status: 'error',
          title: 'Failed to save strategy',
          description: error.message || String(error),
        });
      } finally {
        setIsSavingStrategy(false);
        setPendingStrategy('');
      }
    },
    [currentGameNumber, gameId, isManualMode, totalsPayload, toast, selectedStrategy]
  );

  if (loading) return <Spinner />;

  if (error)
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );

  return (
    <Box>
      <Heading size="lg" mb={4}>
        Draw Details — ID: {drawId} | Game: {gameId}
      </Heading>

      {currentDrawMeta?.manual_draw && (
        <Alert status="info" borderRadius="md" mb={4}>
          <AlertIcon />
          <Box>
            <AlertTitle>Manual draw</AlertTitle>
            <AlertDescription display="block">
              This game uses manual draw times. No result is generated automatically; publish it manually.
              The draw stays open after midnight until a result is set.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Controls row */}
      <HStack spacing={3} align="flex-end" flexWrap="wrap" mb={4}>
        <FormControl minW="280px" flex="1 1 360px">
          <FormLabel>Manual result</FormLabel>
          <Input
            placeholder="Click table cells, or toggle Lowest/Highest, or type here…"
            value={manualResult}
            onChange={handleManualChange}
          />
        </FormControl>

        <FormControl w="120px">
          <FormLabel>Bonus</FormLabel>
          <Select value={bonus} onChange={handleBonusChange}>
            {[...Array(10)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl w="auto">
          <FormLabel>Selection</FormLabel>
          <ButtonGroup isAttached>
            <Button
              onClick={() => handleToggle('lowest')}
              variant={mode === 'lowest' ? 'solid' : 'outline'}
            >
              Lowest
            </Button>
            <Button
              onClick={() => handleToggle('highest')}
              variant={mode === 'highest' ? 'solid' : 'outline'}
            >
              Highest
            </Button>
          </ButtonGroup>
        </FormControl>

        <FormControl w="auto">
          <FormLabel>Auto Load Next Draw</FormLabel>
          <Switch
            colorScheme="blue"
            isChecked={isAutoAdvance}
            onChange={(e) => setIsAutoAdvance(e.target.checked)}
          />
        </FormControl>

        <FormControl w="auto">
          <FormLabel>Manual Mode</FormLabel>
          <Switch
            colorScheme="purple"
            isChecked={isManualMode}
            onChange={(e) => setIsManualMode(e.target.checked)}
          />
        </FormControl>

        <FormControl w="auto">
          <FormLabel>&nbsp;</FormLabel>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isDisabled={isSettingsLocked}
            title={isSettingsLocked ? 'Settings locked for this draw' : undefined}
          >
            Save
          </Button>
        </FormControl>
      </HStack>

      {capBlock && (
        <Alert status="error" alignItems="flex-start" borderRadius="md" mb={4}>
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>
              Manual result blocked by the daily negative-win limit
              {capBlock.bonus ? ` (x${capBlock.bonus})` : ''}
            </AlertTitle>
            <AlertDescription display="block">
              {capBlock.blockedResults.length ? (
                capBlock.blockedResults.map((b) => (
                  <Box key={b.result} mt={1}>
                    <Text as="span" fontWeight="semibold">
                      {b.result}:
                    </Text>{' '}
                    {(b.violations || [])
                      .map(
                        (v) =>
                          `player ${v.player_id} would win ${Number(v.win).toFixed(2)}, only ${Number(v.room).toFixed(2)} left today`
                      )
                      .join('; ')}
                  </Box>
                ))
              ) : (
                <Text>{capBlock.message}</Text>
              )}
              <Text mt={2} fontSize="sm">
                Nothing was saved. Pick a different result or a lower bonus.
              </Text>
            </AlertDescription>
          </Box>
          <Button size="sm" variant="ghost" onClick={() => setCapBlock(null)}>
            Dismiss
          </Button>
        </Alert>
      )}

      <Box
        mb={6}
        p={4}
        bg="white"
        borderRadius="md"
        boxShadow="sm"
        border="1px solid"
        borderColor="gray.100"
      >
        <Flex justify="space-between" align="center" flexWrap="wrap" rowGap={2}>
          <Heading size="md">Result Generation Logic</Heading>
          <Text fontSize="sm" color="gray.600">
            Mode:{' '}
            <Text as="span" fontWeight="semibold">
              {strategyModeLabel}
            </Text>
            {currentGameNumber ? ` • Game ${currentGameNumber}` : ''}
          </Text>
          <Box textAlign="right">
            <Text fontSize="xs" textTransform="uppercase" color="gray.500" letterSpacing="wide">
              Draw Ends In
            </Text>
            <Text fontWeight="bold" fontSize="lg" color={countdownColor}>
              {formatCountdown(safeRemainingMs)}
            </Text>
          </Box>
        </Flex>

        <Wrap mt={3} spacing={3}>
          {STRATEGY_PILLS.map((pill) => {
            const isActive = selectedStrategy === pill;
            return (
              <WrapItem key={pill}>
                <Button
                  size="sm"
                  borderRadius="full"
                  variant={isActive ? 'solid' : 'outline'}
                  colorScheme={isActive ? 'blue' : 'gray'}
                  onClick={() => handleStrategySelect(pill)}
                  isLoading={isSavingStrategy && pendingStrategy === pill}
                  isDisabled={isSettingsLocked || (!currentGameNumber && !gameId)}
                  title={
                    isSettingsLocked
                      ? 'Settings locked for this draw'
                      : !currentGameNumber && !gameId
                      ? 'Game number is unavailable'
                      : undefined
                  }
                >
                  {pill.replace(/_/g, ' ')}
                </Button>
              </WrapItem>
            );
          })}
        </Wrap>

        {summaryMetrics.length > 0 && (
          <Flex mt={4} gap={6} wrap="wrap">
            {summaryMetrics.map(({ label, value, isPercent, color }) => (
              <Box key={label}>
                <Text fontSize="xs" textTransform="uppercase" color="gray.500" letterSpacing="wide">
                  {label}
                </Text>
                <Text fontWeight="semibold" fontSize="md" color={color}>
                  {value === null || value === undefined
                    ? '—'
                    : isPercent
                    ? `${formatNumber(value, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })}%`
                    : formatNumber(value)}
                </Text>
              </Box>
            ))}
          </Flex>
        )}
      </Box>

      <GenericTable columns={columns} data={rows} initialPageSize={20} />

      {manualBlockTrueRows.length > 0 && (
        <Box mt={8}>
          <Heading size="md" mb={2}>
            Manual Blocked Players
          </Heading>
          <GenericTable columns={columns} data={manualBlockTrueRows} initialPageSize={manualBlockTrueRows.length} />
        </Box>
      )}

      {manualBlockFalseRows.length > 0 && (
        <Box mt={8}>
          <Heading size="md" mb={2}>
            Non-blocked Players
          </Heading>
          <GenericTable columns={columns} data={manualBlockFalseRows} initialPageSize={manualBlockFalseRows.length} />
        </Box>
      )}

      {manualNtpTables.length > 0 && (
        <Box mt={8}>
          <Heading size="md" mb={4}>
            Manual NTP Players
          </Heading>
          {manualNtpTables.map((entry) => (
            <Box key={entry.playerId} mb={6}>
              <Heading size="sm" mb={2}>
                {entry.playerName}
              </Heading>
              <GenericTable columns={columns} data={entry.data} initialPageSize={entry.data.length || 10} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DrawDetails;
